import { noop } from '@capsicum/common';
import { type StaticImageData } from 'next/image';
import { type CSSProperties, isValidElement, type PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

import { Button } from '../../atoms/buttons/button/Button';
import { TextButton } from '../../atoms/buttons/textButton/TextButton';
import { Image } from '../../atoms/image/Image';
import { ExternalLinkIcon } from '../../icons/ExternalLink';

const DEFAULT_IMAGE_SIZE = { height: 164, width: 263 };

export type GenericDrawerProps = PropsWithChildren<{
	buttonDisabled?: boolean;
	buttonText?: string;
	caption?: Array<JSX.Element | string> | JSX.Element | string;
	description?: Array<JSX.Element | string> | JSX.Element | string;
	elementStyles?: CSSProperties;
	image?: JSX.Element | StaticImageData | string;
	imageSize?: { height: number; width: number };
	isButtonFullWidth?: boolean;
	isButtonOnRow?: boolean;
	isExternalLink?: boolean;
	onButtonPress?: () => void;
	onSecondaryActionOnButtonPress?: () => void;
	secondaryActionButtonDisabled?: boolean;
	secondaryActionButtonText?: string;
	subtitle?: Array<JSX.Element | string> | JSX.Element | string;
	testID: string;
	title?: Array<JSX.Element | string> | JSX.Element | string;
	warning?: boolean;
}>;

export type ButtonWrapperProps = {
	$isButtonOnRow: boolean;
};

/**
 * GenericDrawer is the Template to create basics drawers.
 *
 * @param {GenericDrawerProps} props - The props of the component
 * @param {string | JSX.Element | (string | JSX.Element)[]} props.title The title of the drawer
 * @param {string | JSX.Element | (string | JSX.Element)[]} props.description the description of a drawer
 * @param {string | StaticImageData | JSX.Element} props.image The source of the image or a React component
 * @param {string} props.testID the ID to enable e2e framework to grab this element
 * @param {string | JSX.Element | (string | JSX.Element)[]} props.buttonText The text of the button
 * @param {boolean} props.isButtonFullWidth style the button with full width
 * @param {boolean} props.buttonDisabled Boolean to show the button enable/disable
 * @param {() => void} props.onButtonPress The callback function on purchase-token button click
 * @returns {JSX.Element} This component
 */
export const GenericDrawer = ({
	buttonDisabled,
	buttonText,
	caption,
	children,
	description,
	elementStyles,
	image,
	imageSize,
	isButtonFullWidth = false,
	isButtonOnRow = true,
	isExternalLink,
	onButtonPress = noop,
	onSecondaryActionOnButtonPress = noop,
	secondaryActionButtonDisabled,
	secondaryActionButtonText,
	subtitle,
	testID,
	title,
	warning = false
}: GenericDrawerProps) => {
	const hasButtons = !!buttonText || !!secondaryActionButtonText;

	return (
		<S.Wrapper data-testid={`${testID}-drawer`} style={elementStyles}>
			{image &&
				(isValidElement(image) ? (
					image
				) : (
					<S.ImageWrapper>
						<S.StyledImage
							$height={imageSize?.height || DEFAULT_IMAGE_SIZE.height}
							$isCustomSize={!!imageSize}
							$width={imageSize?.width || DEFAULT_IMAGE_SIZE.width}
							alt={`${testID}-image`}
							height={imageSize?.height || DEFAULT_IMAGE_SIZE.height}
							src={image as StaticImageData | string}
							width={imageSize?.width || DEFAULT_IMAGE_SIZE.width}
						/>
					</S.ImageWrapper>
				))}
			{(title || description || caption) && (
				<S.TextWrapper>
					{title && <S.Title $warning={warning}>{title}</S.Title>}
					{subtitle && <S.Subtitle $warning={warning}>{subtitle}</S.Subtitle>}
					{description && <S.Description>{description}</S.Description>}
					{caption && <S.Caption>{caption}</S.Caption>}
				</S.TextWrapper>
			)}
			{children}
			{hasButtons && (
				<S.ButtonWrapper $isButtonOnRow={isButtonOnRow}>
					<Button
						color={warning ? 'destructive' : 'brand'}
						disabled={buttonDisabled}
						fullWidth={isButtonFullWidth}
						testID={`${testID}-button`}
						text={buttonText}
						onClick={() => onButtonPress()}
					/>
					{secondaryActionButtonText && !!onSecondaryActionOnButtonPress && (
						<TextButton
							color="neutral"
							disabled={secondaryActionButtonDisabled}
							testID={`${testID}-secondaryActionButton`}
							text={secondaryActionButtonText}
							onClick={() => onSecondaryActionOnButtonPress()}
							{...(isExternalLink && { iconEnd: { icon: { src: ExternalLinkIcon } } })}
						/>
					)}
				</S.ButtonWrapper>
			)}
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		max-width: ${({ theme }) => `${theme.breakpoints.points.tabletSmallDesktop.pageWidth}px`};
		height: 100%;
		width: 100%;
		margin: auto;
	`;

	export const ImageWrapper = styled.div`
		display: flex;
		justify-content: center;
		align-items: center;
		margin-top: ${({ theme }) => theme.spacings.small};
	`;

	export const StyledImage = styled(Image)<{ $height: number; $isCustomSize: boolean; $width: number }>`
		max-width: ${({ $width }) => `${$width}px`};
		max-height: ${({ $height }) => `${$height}px`};

		${({ $isCustomSize }) =>
			$isCustomSize
				? css`
						object-fit: contain;
						width: 100%;
					`
				: css`
						width: auto;
						height: auto;
					`}
	`;

	export const TextWrapper = styled.div`
		margin-top: ${({ theme }) => theme.spacings.large};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Title = styled.p<{ $warning: boolean }>`
		${({ theme }) => theme.fonts.headings.title6};
		text-align: center;
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
		color: ${({ $warning, theme }) => $warning && theme.colors.backgrounds.error.enabled};
		white-space: pre-line;
	`;

	export const Subtitle = styled.p<{ $warning: boolean }>`
		${({ theme }) => theme.fonts.text.text1};
		text-align: center;
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
		color: ${({ $warning, theme }) => $warning && theme.colors.backgrounds.error.enabled};
		white-space: pre-line;
	`;

	export const Description = styled.p`
		${({ theme }) => theme.fonts.text.text3};
		text-align: center;
		margin-top: ${({ theme }) => theme.spacings.xsmall};
		margin: 0;
		white-space: pre-line;
	`;

	export const Caption = styled.p`
		${({ theme }) => theme.fonts.text.caption2};
		text-align: center;
		margin-top: ${({ theme }) => theme.spacings.large};
		white-space: pre-line;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ButtonWrapper = styled.div<ButtonWrapperProps>`
		display: flex;
		flex-direction: ${props => (props.$isButtonOnRow ? 'row' : 'column')};
		justify-content: center;
		align-items: center;
		margin-top: ${({ theme }) => theme.spacings.large};
		gap: ${({ theme }) => theme.spacings.medium};
	`;
}
